import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FiCalendar, FiUser } from "react-icons/fi";
import { useAppSelector } from "../../store/hooks";
import api from "../../utils/api";
import SurgerySlotPicker from "../../components/surgery/SurgerySlotPicker";
import {
  DaySlotDto,
  formatLongDay,
  formatSlotLabel,
  startOfDay,
  toYmd,
} from "../../utils/surgeryBookingCalendar";
import {
  SURGERY_CLIENT_REGION,
  SURGERY_CLIENT_TIMEZONE,
  isUkSurgeryRegion,
  ukTodayDate,
} from "../../utils/surgeryTimezones";
import { useToast } from "../../components/utilities/Toast";

const emptySlots = (): DaySlotDto[] =>
  Array.from({ length: 24 }, (_, hour) => ({
    hour,
    bookedCount: 0,
    capacity: 1,
    available: true,
    myBooking: false,
    isPast: false,
  }));

const BookSurgery = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { showToast } = useToast();
  // Calendar "today" follows UK (Europe/London), not the browser zone
  const todayUk = useMemo(() => ukTodayDate(), []);
  const region = SURGERY_CLIENT_REGION;
  const tzLabel = SURGERY_CLIENT_TIMEZONE;

  const [viewMonth, setViewMonth] = useState(() => {
    const d = ukTodayDate();
    d.setDate(1);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(() => ukTodayDate());
  const [selectedHours, setSelectedHours] = useState<number[]>([]);
  const [slots, setSlots] = useState<DaySlotDto[]>(emptySlots);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");
  const [myBookings, setMyBookings] = useState<
    Array<{
      id: number;
      client_local_date?: string | null;
      client_local_hour?: number | null;
      client_local_label?: string | null;
      notes: Array<{ id: number; note: string; created_at?: string }>;
    }>
  >([]);

  const selectedLabels = selectedHours
    .slice()
    .sort((a, b) => a - b)
    .map(formatSlotLabel);

  const fetchSlots = useCallback(async (date: Date) => {
    setLoading(true);
    try {
      const res = await api.get("/api/surgery-booking/slots", {
        params: { date: toYmd(date) },
      });
      const data = res.data?.data;
      setSlots(Array.isArray(data?.slots) ? data.slots : emptySlots());
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to load slots",
        "error",
      );
      setSlots(emptySlots());
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchMyBookings = useCallback(async () => {
    try {
      const res = await api.get("/api/surgery-booking/my-bookings");
      setMyBookings(
        Array.isArray(res.data?.data?.bookings) ? res.data.data.bookings : [],
      );
    } catch {
      setMyBookings([]);
    }
  }, []);

  useEffect(() => {
    fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  if (
    user?.role !== "User" ||
    !user.verifySurgeryBooking ||
    !isUkSurgeryRegion(user.region)
  ) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  const labName = user.organisation?.trim() || user.user_name || "Your lab";
  const today = startOfDay(todayUk);

  const dayNotes = myBookings.filter(
    (b) =>
      b.client_local_date === toYmd(selectedDate) &&
      Array.isArray(b.notes) &&
      b.notes.length > 0,
  );

  const goPrevMonth = () => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goNextMonth = () => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const selectDate = (date: Date) => {
    const day = startOfDay(date);
    if (day.getTime() < today.getTime()) return;
    setSelectedDate(day);
    setSelectedHours([]);
    if (
      day.getMonth() !== viewMonth.getMonth() ||
      day.getFullYear() !== viewMonth.getFullYear()
    ) {
      setViewMonth(new Date(day.getFullYear(), day.getMonth(), 1));
    }
  };

  const toggleHour = (hour: number) => {
    setSelectedHours((prev) =>
      prev.includes(hour) ? prev.filter((h) => h !== hour) : [...prev, hour],
    );
  };

  const handleConfirm = async () => {
    if (selectedHours.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/api/surgery-booking/book", {
        date: toYmd(selectedDate),
        hours: selectedHours,
        note: note.trim() || undefined,
      });
      showToast(
        `Booked ${selectedHours.length} slot${selectedHours.length === 1 ? "" : "s"}`,
        "success",
      );
      setSelectedHours([]);
      setNote("");
      await Promise.all([fetchSlots(selectedDate), fetchMyBookings()]);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Booking failed",
        "error",
      );
      await fetchSlots(selectedDate);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Book Surgery</title>
      </Helmet>

      <div className="w-full px-3 sm:px-4 md:px-6 py-3 md:py-5 flex justify-center">
        <div className="w-full max-w-[90%] rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden text-[90%]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 md:px-6 pt-4 md:pt-5 pb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
                <FiCalendar className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg md:text-xl font-semibold text-gray-900">
                    Book Surgery
                  </h1>
                  <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
                    1 hr
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                  <FiUser className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate">
                    {labName}
                    <span className="text-gray-300 mx-1">·</span>
                    Times in {region} ({tzLabel})
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                Your booking
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-900" />
                Selected
              </span>
            </div>
          </div>

          <div className="h-px bg-gray-100" />

          <SurgerySlotPicker
            viewMonth={viewMonth}
            selectedDate={selectedDate}
            today={today}
            slots={slots}
            selectedHours={selectedHours}
            loading={loading}
            onPrevMonth={goPrevMonth}
            onNextMonth={goNextMonth}
            onSelectDate={selectDate}
            onToggleHour={toggleHour}
          />

          <div className="border-t border-gray-100 px-4 md:px-6 py-3 bg-white">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Note for surgery{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={5000}
              placeholder="Add any details for the surgery team…"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 resize-y min-h-[64px]"
            />
          </div>

          {dayNotes.length > 0 ? (
            <div className="border-t border-gray-100 px-4 md:px-6 py-3 bg-gray-50/80">
              <p className="text-xs font-medium text-gray-600 mb-2">
                Your notes for {formatLongDay(selectedDate)}
              </p>
              <ul className="space-y-2">
                {dayNotes.map((b) =>
                  b.notes.map((n) => (
                    <li
                      key={n.id}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700"
                    >
                      <span className="text-gray-400">
                        {b.client_local_hour != null
                          ? formatSlotLabel(b.client_local_hour)
                          : "Slot"}
                        {" · "}
                      </span>
                      {n.note}
                    </li>
                  )),
                )}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2.5 border-t border-gray-100 px-4 md:px-6 py-3 md:py-4 bg-gray-50/60">
            <p className="text-xs md:text-sm text-gray-500 min-w-0 sm:truncate text-center sm:text-left">
              {selectedHours.length === 0
                ? "Select one or more open time slots"
                : `${formatLongDay(selectedDate)} · ${selectedLabels.join(", ")} (${selectedHours.length})`}
            </p>
            <button
              type="button"
              disabled={selectedHours.length === 0 || submitting}
              onClick={handleConfirm}
              className={[
                "w-full sm:w-auto shrink-0 rounded-lg px-6 py-2.5 text-xs md:text-sm font-semibold transition-colors",
                selectedHours.length === 0 || submitting
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-900 text-white hover:bg-black",
              ].join(" ")}
            >
              {submitting
                ? "Booking…"
                : `Confirm Booking${selectedHours.length > 0 ? ` (${selectedHours.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookSurgery;
